// TestList.js
import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, deleteDoc } from 'firebase/firestore';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { 
  Fab, 
  IconButton, 
  Typography,
  Box,
  Paper,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  OutlinedInput
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {db} from '../Configuration';
import dayjs from 'dayjs';
import AddIcon from "@mui/icons-material/Add";
import AddTest from "./AddTest";
import EditTestDialog from "./EditTestDialog";
import { useFirebaseData } from '../contexts/FirebaseDataContext';
import { isUrduText, getUrduSxProps } from '../utils/urduFont';

const TestList = () => {
  const { campuses: contextCampuses, initialLoadComplete } = useFirebaseData();
  const [tests, setTests] = useState([]);
  const [openTest, setOpenTest] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [selectedCampus, setSelectedCampus] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleOpenDialog = () => setOpenTest(true);
  const handleCloseDialog = () => {
    fetchTests();
    setOpenTest(false);
  };

  const handleEditDialogOpen = (test) => {
    setSelectedTest(test);
    setOpenEditDialog(true);
  };

  const handleEditDialogClose = () => {
    setOpenEditDialog(false);
    setSelectedTest(null);
  };

  const fetchCourses = async () => {
    try {
      const courseCollection = collection(db, 'Courses');
      const courseSnapshot = await getDocs(courseCollection);
      const courseData = {};
      courseSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        courseData[data?.id] = data.name;
      });
      return courseData;
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchTests = async () => {
    try {
      let courses =  await fetchCourses(); // Fetch courses before tests to ensure course names are available

      const testCollection = collection(db, 'Tests');
      const testSnapshot = await getDocs(testCollection);
      let testList = testSnapshot.docs.map((doc) => {
        const data = doc.data();
        const formattedDate = data.date ? dayjs(data.date).format('YYYY-MM-DD') : 'N/A';
        return {
          id: doc.id,
          ...data,
          courseName: courses[data.courseId] || 'Unknown Course', // Use course name based on courseId
          date: formattedDate,
        };
      });

      // Filter tests by selected campus
      if (selectedCampus && testList.length > 0) {
        testList = testList.filter((test) =>
          test.campusCodes && test.campusCodes.includes(selectedCampus)
        );
      } else if (!selectedCampus) {
        // If no campus selected, show nothing
        testList = [];
      }

      // Sort tests by sequence number for proper ordering
      testList.sort((a, b) => {
        const seqA = a.sequence || 999; // Default to 999 if no sequence
        const seqB = b.sequence || 999; // Default to 999 if no sequence
        return seqA - seqB;
      });

      setTests(testList);
      
      if (testList.length === 0) {
        showSnackbar('No tests found for the selected criteria', 'info');
      }
    } catch (error) {
      console.error('Error fetching tests:', error);
      showSnackbar('Error loading tests. Please try again.', 'error');
    }
  };

  // Auto-select campus if only one is available
  useEffect(() => {
    if (initialLoadComplete && contextCampuses.length === 1 && !selectedCampus) {
      setSelectedCampus(contextCampuses[0].campusCode);
    }
  }, [initialLoadComplete, contextCampuses, selectedCampus]);

  useEffect(() => {
    fetchTests();
  }, [selectedCampus]);

  const handleCampusChange = (event) => {
    setSelectedCampus(event.target.value);
  };

  const handleDeleteTest = async (id) => {
    if (window.confirm('Are you sure you want to delete this test?')) {
      try {
        await deleteDoc(doc(db, 'Tests', id));
        setTests((prevTests) => prevTests.filter((test) => test.id !== id));
        showSnackbar('Test deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting test:', error);
        showSnackbar('Error deleting test. Please try again.', 'error');
      }
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const columns = [
    { 
      field: 'testName', 
      headerName: 'Test Name', 
      width: 200,
      renderCell: (params) => {
        const value = params.value || '';
        const urdu = isUrduText(value);
        return (
          <Typography
            className={urdu ? 'urdu' : ''}
            variant="body2"
            sx={{
              ...getUrduSxProps(value),
              direction: 'ltr',
              textAlign: 'left'
            }}
          >
            {value}
          </Typography>
        );
      }
    },
    { field: 'courseName', headerName: 'Course Name', width: 200 }, // Display course name instead of courseId
    { field: 'maxScore', headerName: 'Max Score', width: 150 },
    { field: 'date', headerName: 'Test Date', width: 200 }, { 
      field: 'campusCodes', 
      headerName: 'Campus Codes', 
      width: 250, 
      renderCell: (params) => (
        <div>
          {Array.isArray(params.value) ? params.value.join(", ") : params.value}
        </div>
      ) 
    }, //
    {
      field: 'actions',
      disableExport:true,
      headerName: 'Actions',
      width: 200,
      renderCell: (params) => (
        <>
          <IconButton
            color="primary"
            onClick={() => handleEditDialogOpen(params.row)}
            aria-label="edit"
            sx={{
              borderRadius: 2,
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="secondary"
            onClick={() => handleDeleteTest(params.row.id)}
            aria-label="delete"
            sx={{
              borderRadius: 2,
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3, background: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700, 
            color: '#2E7D32',
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          📝 Tests Configuration
        </Typography>
        <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
          Manage test configurations, set maximum scores, and assign to courses
        </Typography>
      </Box>

      {/* Campus Selection */}
      <Paper elevation={2} sx={{ mb: 3, p: 2, borderRadius: 2 }}>
        <FormControl sx={{ minWidth: 300, maxWidth: 400 }}>
          <InputLabel sx={{ color: '#4CAF50' }}>Select Campus</InputLabel>
          <Select
            value={selectedCampus}
            onChange={handleCampusChange}
            input={<OutlinedInput label="Select Campus" />}
            disabled={contextCampuses.length === 1}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: '#4CAF50',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2E7D32',
                },
              },
            }}
          >
            {contextCampuses.map((campus) => (
              <MenuItem key={campus.id} value={campus.campusCode}>
                {campus.campusName} ({campus.campusCode})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {!selectedCampus && (
        <Paper sx={{ p: 4, textAlign: 'center', mb: 3 }}>
          <Typography variant="h6" color="text.secondary">
            Please select a campus to view tests
          </Typography>
        </Paper>
      )}

      {/* Tests Data Grid Card */}
      {selectedCampus && (
      <Paper 
        elevation={2} 
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 3,
          background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
          border: '1px solid rgba(76, 175, 80, 0.1)'
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600, 
            color: '#2E7D32',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          📊 Test Records
        </Typography>
        
        <DataGrid
          rows={tests}
          columns={columns}
          slots={{ toolbar: GridToolbar }}
          sx={{
            border: 'none',
            height: 600,
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(76, 175, 80, 0.1)',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8f9fa',
              borderBottom: '2px solid #4CAF50',
            },
          }}
        />
      </Paper>
      )}

      {/* Floating Action Button */}
      <Box sx={{ 
        position: 'fixed', 
        bottom: 20, 
        right: 20, 
        zIndex: 1000
      }}>
        <Fab 
          color="primary" 
          aria-label="add" 
          onClick={handleOpenDialog}
          sx={{
            backgroundColor: '#4CAF50',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#2E7D32',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(76, 175, 80, 0.3)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          <AddIcon />
        </Fab>
      </Box>

      <AddTest open={openTest} onClose={handleCloseDialog} />
      {selectedTest && (
        <EditTestDialog
          open={openEditDialog}
          onClose={handleEditDialogClose}
          test={selectedTest}
        />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ zIndex: 99999 }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TestList;
